import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Loader2, ShieldCheck, CheckCircle } from 'lucide-react';
import { NeoButton, NeoInput } from '../NexusUI';

interface MfaEnrollmentProps {
  onEnrolled: () => void;
  onCancel: () => void;
}

export function MfaEnrollment({ onEnrolled, onCancel }: MfaEnrollmentProps) {
  const [factorId, setFactorId] = useState<string | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    async function startEnrollment() {
      try {
        // 1. List existing factors
        const { data: factorsData, error: listError } = await supabase!.auth.mfa.listFactors();
        if (listError) throw listError;
        
        // 2. Unenroll any existing unverified totp factors to get a fresh QR code
        if (factorsData && factorsData.all.length > 0) {
          for (const factor of factorsData.all) {
            if (factor.status === 'unverified') {
              const unenrollRes = await supabase!.auth.mfa.unenroll({ factorId: factor.id });
              if (unenrollRes.error) console.error("Unenroll error:", unenrollRes.error);
            }
          }
        }
        
        // 3. Create a new factor with a friendly name
        const { data, error } = await supabase!.auth.mfa.enroll({
          factorType: 'totp',
          friendlyName: 'Authenticator App ' + new Date().getTime(),
          issuer: 'Rezilyens Enterprise'
        });
        
        if (error) throw error;
        
        if (data.totp.qr_code) {
          let svgStr = data.totp.qr_code;
          let finalDataUri = '';
          
          if (svgStr.startsWith('data:image/svg+xml;base64,')) {
            // Already a base64 data URI
            finalDataUri = svgStr;
          } else {
            if (svgStr.startsWith('data:image/svg+xml')) {
              // It's a non-base64 data URI, extract the raw SVG
              const parts = svgStr.split(',');
              svgStr = decodeURIComponent(parts.slice(1).join(','));
            }
            
            // Ensure xmlns is present for img tag rendering
            if (!svgStr.includes('xmlns=')) {
              svgStr = svgStr.replace('<svg ', '<svg xmlns="http://www.w3.org/2000/svg" ');
            }
            
            // Safely encode the SVG string to Base64
            const base64Svg = btoa(unescape(encodeURIComponent(svgStr)));
            finalDataUri = `data:image/svg+xml;base64,${base64Svg}`;
          }
          
          setQrCode(finalDataUri);
          setFactorId(data.id);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to initialize MFA setup.');
      }
    }
    
    startEnrollment();
  }, []);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!factorId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const challenge = await supabase!.auth.mfa.challenge({ factorId });
      if (challenge.error) throw challenge.error;
      
      const verify = await supabase!.auth.mfa.verify({
        factorId,
        challengeId: challenge.data.id,
        code: verifyCode
      });
      
      if (verify.error) throw verify.error;
      
      setSuccess(true);
      setTimeout(() => {
        onEnrolled();
      }, 1500);
      
    } catch (err: any) {
      setError(err.message || 'Invalid verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="flex flex-col items-center justify-center p-6 text-center space-y-4 animate-in fade-in zoom-in duration-300">
        <div className="h-16 w-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-2">
          <CheckCircle className="h-8 w-8" />
        </div>
        <h2 className="text-xl font-bold">MFA Activated Successfully</h2>
        <p className="text-muted-foreground text-sm">Your account is now secure. Redirecting...</p>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
      <div className="mb-6 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 mb-4">
          <ShieldCheck className="h-6 w-6 text-primary" />
        </div>
        <h2 className="text-xl font-bold tracking-tight">Secure Your Account</h2>
        <p className="text-sm text-muted-foreground mt-2">
          Scan the QR code with an authenticator app (like Microsoft Authenticator) to enable Multi-Factor Authentication.
        </p>
      </div>

      {!qrCode && error && (
        <div className="mb-6 rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-500 text-center font-medium">
          {error}
        </div>
      )}

      {!qrCode ? (
        <div className="flex justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex justify-center bg-white p-4 rounded-xl border border-border mx-auto w-fit shadow-sm">
            <img 
              src={qrCode} 
              alt="Scan this QR code with your Authenticator app"
              className="w-[200px] h-[200px] object-contain"
            />
          </div>
          
          {error && (
            <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-500 text-center font-medium">
              {error}
            </div>
          )}
          
          <form onSubmit={handleVerify} className="space-y-4">
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground ml-2">
                Verification Code
              </label>
              <NeoInput
                type="text"
                placeholder="000000"
                maxLength={6}
                value={verifyCode}
                onChange={(e: any) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                required
              />
            </div>
            
            <NeoButton
              type="submit"
              variant="primary"
              disabled={loading || verifyCode.length < 6}
              className="w-full flex justify-center items-center gap-2"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Verify & Enable
            </NeoButton>
            
            <div className="text-center mt-4">
              <button
                type="button"
                onClick={onCancel}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Sign out
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
